import { useState, type FormEvent, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { CallerCheck, Destination, FlowMessage, FlowMessageUpsert, HoursCheck, HoursCheckUpsert, Lookup } from '@arcnet/contracts';
import { useSession } from '../../lib/auth/session.js';
import {
  useBusinessHours, useCallerChecks, useDeleteTms, useHoursChecks, useLookups, useMessages, useSaveHoursCheck, useSaveMessage, useSchedules,
} from '../../lib/api/hooks-tms.js';
import { api } from '../../lib/api/client.js';
import {
  Badge, EditorHead, Empty, ErrorBanner, SectionHead, Segmented, SubHead, TableSkeleton,
} from '../../components/ui/Primitives.js';
import { Icon } from '../../components/ui/Icon.js';
import { RowAction, openRowProps } from '../../components/ui/RowAction.js';
import { useSubroute } from '../../lib/subroute.js';
import { nameSlugs } from '../../lib/openitem.js';
import { UnsavedGuard } from '../../components/ui/UnsavedGuard.js';
import { confirmAction } from '../../components/ui/Confirm.js';
import { DestinationLabel, DestinationPicker } from '../../components/telephony/DestinationPicker.js';
import {
  HoursEditor, blankHours, hoursFromRules, invalidDay, rulesFromHours, type HoursDraft,
} from '../../components/telephony/HoursEditor.js';
import { referrers, useRoutingData } from './usedby.js';
import type { PanelMode } from './flows/panel.js';
import { StepHistory } from './StepHistory.js';
import { MakeRecordingAction } from '../../components/telephony/MakeRecording.js';
import { CallerCheckEditor, LookupEditor } from './CallStepEditors.js';

export { CallerCheckEditor, LookupEditor };

/**
 * Call steps (docs/41 §3), Classic's page for them: messages -- a recording, then on -- and hours
 * checks, open or closed anywhere in a flow. A flow built on the canvas uses these same pieces, so
 * everything made there can be found and changed here too.
 */
export function CallStepsTab({ tenantId }: { tenantId: string }) {
  const mayWrite = useSession((s) => s.can)('flow:write');
  const messages = useMessages(tenantId);
  const hours = useHoursChecks(tenantId);
  const delMessage = useDeleteTms(tenantId, 'messages');
  const delHours = useDeleteTms(tenantId, 'hours-checks');
  const callers = useCallerChecks(tenantId);
  const lookups = useLookups(tenantId);
  const delCaller = useDeleteTms(tenantId, 'caller-checks');
  const delLookup = useDeleteTms(tenantId, 'lookups');
  const callerSlugs = nameSlugs(callers.data?.data ?? [], (c) => c.name);
  const lookupSlugs = nameSlugs(lookups.data?.data ?? [], (l) => l.name);
  const routing = useRoutingData(tenantId);
  /**
   * The open step is in the address (owner, 21 October 2026): /call-steps/messages/after-hours,
   * /call-steps/hours-checks/lunch, or .../new.
   */
  const route = useSubroute();
  const msgSlugs = nameSlugs(messages.data?.data ?? [], (m) => m.name);
  const hourSlugs = nameSlugs(hours.data?.data ?? [], (h) => h.name);
  const [section, slug] = route.rest;
  const editing: { kind: 'message'; row: FlowMessage | null } | { kind: 'hours'; row: HoursCheck | null } | null =
    section === 'messages' && slug === 'new' ? { kind: 'message', row: null }
      : section === 'hours-checks' && slug === 'new' ? { kind: 'hours', row: null }
        : section === 'messages' && slug ? (() => {
          const row = messages.data?.data.find((m) => msgSlugs.get(m.id) === slug);
          return row ? { kind: 'message' as const, row } : null;
        })()
          : section === 'hours-checks' && slug ? (() => {
            const row = hours.data?.data.find((h) => hourSlugs.get(h.id) === slug);
            return row ? { kind: 'hours' as const, row } : null;
          })() : null;
  const setEditing = (e: { kind: 'message'; row: FlowMessage | null } | { kind: 'hours'; row: HoursCheck | null } | null) => {
    if (!e) { route.go([]); return; }
    route.go(e.kind === 'message'
      ? ['messages', e.row ? msgSlugs.get(e.row.id) ?? 'new' : 'new']
      : ['hours-checks', e.row ? hourSlugs.get(e.row.id) ?? 'new' : 'new']);
  };
  // Bumped by a restore from History, so the editor opens again on what was written.
  const [rev, setRev] = useState(0);

  // Caller checks and look ups (docs/41 §16), opened the same way: /call-steps/caller-checks/vips.
  const more = section === 'caller-checks' || section === 'lookups' ? (() => {
    const isCaller = section === 'caller-checks';
    const rows: Array<CallerCheck | Lookup> = (isCaller ? callers.data?.data : lookups.data?.data) ?? [];
    const slugs = isCaller ? callerSlugs : lookupSlugs;
    if (slug === 'new') return { isCaller, row: null };
    const row = rows.find((r) => slugs.get(r.id) === slug);
    return row ? { isCaller, row } : null;
  })() : null;
  if (more) {
    const back = () => route.go([]);
    const row = more.row;
    const history = row && (
      <StepHistory tenantId={tenantId} kind={more.isCaller ? 'caller' : 'lookup'} id={row.id} name={row.name}
        onRestored={() => setRev((r) => r + 1)} />
    );
    return (
      <UnsavedGuard key={`${row?.id ?? 'new'}:${rev}`} readOnly={!mayWrite}>
        {more.isCaller
          ? <CallerCheckEditor tenantId={tenantId} existing={row as CallerCheck | null} onDone={back} headAction={history} />
          : <LookupEditor tenantId={tenantId} existing={row as Lookup | null} onDone={back} headAction={history} />}
      </UnsavedGuard>
    );
  }

  // An editor takes the list's place, so there is one thing on the page (owner, 29 September 2026).
  if (editing?.kind === 'message') {
    const row = editing.row;
    return (
      <UnsavedGuard key={`${row?.id ?? 'new'}:${rev}`} readOnly={!mayWrite}>
        <MessageEditor tenantId={tenantId} existing={row} onDone={() => setEditing(null)}
          headAction={row && (
            <StepHistory tenantId={tenantId} kind="message" id={row.id} name={row.name}
              onRestored={() => setRev((r) => r + 1)} />
          )} />
      </UnsavedGuard>
    );
  }
  if (editing?.kind === 'hours') {
    const row = editing.row;
    return (
      <UnsavedGuard key={`${row?.id ?? 'new'}:${rev}`} readOnly={!mayWrite}>
        <HoursCheckEditor tenantId={tenantId} existing={row} onDone={() => setEditing(null)}
          headAction={row && (
            <StepHistory tenantId={tenantId} kind="hours" id={row.id} name={row.name}
              onRestored={() => setRev((r) => r + 1)} />
          )} />
      </UnsavedGuard>
    );
  }

  const usedBy = (type: string, id: string) => {
    const who = referrers(type, id, routing);
    return who.length === 0 ? <span className="arc-subtle">Not used yet</span> : who.join(', ');
  };

  return (
    <>
      <SectionHead title="Call steps"
        sub="Messages, hours checks, caller checks and look ups: steps a call can pass through on its way somewhere."
        action={mayWrite && (
          <>
            <button type="button" className="arc-btn arc-btn-primary arc-btn-sm" onClick={() => setEditing({ kind: 'message', row: null })}>
              <Icon name="plus" size={13} /> New message
            </button>
            <button type="button" className="arc-btn arc-btn-sm" onClick={() => setEditing({ kind: 'hours', row: null })}>
              <Icon name="plus" size={13} /> New hours check
            </button>
            <button type="button" className="arc-btn arc-btn-sm" onClick={() => route.go(['caller-checks', 'new'])}>
              <Icon name="plus" size={13} /> New caller check
            </button>
            <button type="button" className="arc-btn arc-btn-sm" onClick={() => route.go(['lookups', 'new'])}>
              <Icon name="plus" size={13} /> New look up
            </button>
          </>
        )} />

      {(messages.error || hours.error) && <ErrorBanner error={messages.error ?? hours.error} />}

      <SectionHead level="section" title="Messages" sub="A recording, then on to the next step." />
      <div className="arc-card" style={{ marginBottom: 24 }}>
        <div className="arc-table-wrap">
          <table className="arc-table">
            <thead><tr><th>Name</th><th>Recording</th><th>Then</th><th>Used by</th><th /></tr></thead>
            {messages.data === undefined ? <TableSkeleton query={messages} cols={5} /> : (
              <tbody>
                {messages.data.data.map((m) => (
                  <tr key={m.id} {...openRowProps(() => setEditing({ kind: 'message', row: m }))}>
                    <td style={{ fontWeight: 550 }}>{m.name}</td>
                    <td>{m.media_name ?? <Badge tone="warn"><Icon name="warning" size={11} />needs a recording</Badge>}</td>
                    <td>{m.next_destination
                      ? <DestinationLabel tenantId={tenantId} destination={m.next_destination} />
                      : <span className="arc-subtle">the call ends</span>}</td>
                    <td className="arc-muted">{usedBy('message', m.id)}</td>
                    <td className="arc-td-right">
                      {mayWrite && (
                        <div className="arc-row arc-row-actions" style={{ justifyContent: 'flex-end', gap: 2 }}>
                          <RowAction kind="edit" label={`Edit ${m.name}`} onClick={() => setEditing({ kind: 'message', row: m })} />
                          <RowAction kind="delete" label={`Delete ${m.name}`} onClick={() => {
                            void confirmAction({ title: `Delete the message '${m.name}'?`, action: 'Delete', danger: true })
                              .then((ok) => { if (ok) delMessage.mutate(m.id); });
                          }} />
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            )}
          </table>
        </div>
        {messages.data?.data.length === 0 && (
          <Empty icon="media" title="No messages" hint="A message plays a recording to the caller, then sends them on." />
        )}
        {delMessage.error && <ErrorBanner error={delMessage.error} />}
      </div>

      <SectionHead level="section" title="Hours checks" sub="Open or closed, anywhere in a flow." />
      <div className="arc-card" style={{ marginBottom: 24 }}>
        <div className="arc-table-wrap">
          <table className="arc-table">
            <thead><tr><th>Name</th><th>Hours</th><th>Open</th><th>Closed</th><th>Now</th><th>Used by</th><th /></tr></thead>
            {hours.data === undefined ? <TableSkeleton query={hours} cols={7} /> : (
              <tbody>
                {hours.data.data.map((h) => (
                  <tr key={h.id} {...openRowProps(() => setEditing({ kind: 'hours', row: h }))}>
                    <td style={{ fontWeight: 550 }}>{h.name}</td>
                    <td>{h.hours.mode === 'own' ? 'Its own hours' : `Schedule · ${h.hours.schedule_name ?? 'deleted'}`}</td>
                    <td><DestinationLabel tenantId={tenantId} destination={h.open_destination} /></td>
                    <td><DestinationLabel tenantId={tenantId} destination={h.closed_destination} /></td>
                    <td>{h.hours.open_now === null ? <span className="arc-subtle">—</span>
                      : <Badge tone={h.hours.open_now ? 'ok' : 'muted'}><span className="arc-dot" />{h.hours.open_now ? 'open' : 'closed'}</Badge>}</td>
                    <td className="arc-muted">{usedBy('hours', h.id)}</td>
                    <td className="arc-td-right">
                      {mayWrite && (
                        <div className="arc-row arc-row-actions" style={{ justifyContent: 'flex-end', gap: 2 }}>
                          <RowAction kind="edit" label={`Edit ${h.name}`} onClick={() => setEditing({ kind: 'hours', row: h })} />
                          <RowAction kind="delete" label={`Delete ${h.name}`} onClick={() => {
                            void confirmAction({ title: `Delete the hours check '${h.name}'?`, action: 'Delete', danger: true })
                              .then((ok) => { if (ok) delHours.mutate(h.id); });
                          }} />
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            )}
          </table>
        </div>
        {hours.data?.data.length === 0 && (
          <Empty icon="clock" title="No hours checks" hint="An hours check sends a call one way while open and another while closed." />
        )}
        {delHours.error && <ErrorBanner error={delHours.error} />}
      </div>

      <SectionHead level="section" title="Caller checks" sub="Callers on a list one way, everyone else another." />
      <div className="arc-card" style={{ marginBottom: 24 }}>
        <div className="arc-table-wrap">
          <table className="arc-table">
            <thead><tr><th>Name</th><th>On the list</th><th>Then</th><th>Anyone else</th><th>Used by</th><th /></tr></thead>
            {callers.data === undefined ? <TableSkeleton query={callers} cols={6} /> : (
              <tbody>
                {callers.data.data.map((c) => {
                  const open = () => route.go(['caller-checks', callerSlugs.get(c.id) ?? 'new']);
                  const n = c.numbers.length;
                  return (
                    <tr key={c.id} {...openRowProps(open)}>
                      <td style={{ fontWeight: 550 }}>{c.name}</td>
                      <td>{n === 0 ? '' : `${n} ${n === 1 ? 'number' : 'numbers'}`}{c.match_withheld ? `${n ? ', ' : ''}hidden numbers` : ''}</td>
                      <td><DestinationLabel tenantId={tenantId} destination={c.match_destination} /></td>
                      <td><DestinationLabel tenantId={tenantId} destination={c.no_match_destination} /></td>
                      <td className="arc-muted">{usedBy('caller', c.id)}</td>
                      <td className="arc-td-right">
                        {mayWrite && (
                          <div className="arc-row arc-row-actions" style={{ justifyContent: 'flex-end', gap: 2 }}>
                            <RowAction kind="edit" label={`Edit ${c.name}`} onClick={open} />
                            <RowAction kind="delete" label={`Delete ${c.name}`} onClick={() => {
                              void confirmAction({ title: `Delete the caller check '${c.name}'?`, action: 'Delete', danger: true })
                                .then((ok) => { if (ok) delCaller.mutate(c.id); });
                            }} />
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            )}
          </table>
        </div>
        {callers.data?.data.length === 0 && (
          <Empty icon="contacts" title="No caller checks" hint="A caller check sends callers on a list, such as your biggest customers, one way and everyone else another." />
        )}
        {delCaller.error && <ErrorBanner error={delCaller.error} />}
      </div>

      <SectionHead level="section" title="Look ups" sub="Asks your own system, while the call waits, where this caller goes." />
      <div className="arc-card">
        <div className="arc-table-wrap">
          <table className="arc-table">
            <thead><tr><th>Name</th><th>Asks</th><th>Reads</th><th>Answers</th><th>Used by</th><th /></tr></thead>
            {lookups.data === undefined ? <TableSkeleton query={lookups} cols={6} /> : (
              <tbody>
                {lookups.data.data.map((l) => {
                  const open = () => route.go(['lookups', lookupSlugs.get(l.id) ?? 'new']);
                  let host = l.url;
                  try { host = new URL(l.url).hostname; } catch { /* shown as typed */ }
                  return (
                    <tr key={l.id} {...openRowProps(open)}>
                      <td style={{ fontWeight: 550 }}>{l.name}</td>
                      <td className="arc-mono" style={{ fontSize: 12 }}>{host}</td>
                      <td className="arc-mono" style={{ fontSize: 12 }}>{l.field}</td>
                      <td>{l.cases.map((c) => c.value).join(', ')}</td>
                      <td className="arc-muted">{usedBy('lookup', l.id)}</td>
                      <td className="arc-td-right">
                        {mayWrite && (
                          <div className="arc-row arc-row-actions" style={{ justifyContent: 'flex-end', gap: 2 }}>
                            <RowAction kind="edit" label={`Edit ${l.name}`} onClick={open} />
                            <RowAction kind="delete" label={`Delete ${l.name}`} onClick={() => {
                              void confirmAction({ title: `Delete the look up '${l.name}'?`, action: 'Delete', danger: true })
                                .then((ok) => { if (ok) delLookup.mutate(l.id); });
                            }} />
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            )}
          </table>
        </div>
        {lookups.data?.data.length === 0 && (
          <Empty icon="globe" title="No look ups" hint="A look up asks your CRM or another system about the caller, and sends the call where its answer says." />
        )}
        {delLookup.error && <ErrorBanner error={delLookup.error} />}
      </div>
    </>
  );
}

/** Recordings a caller can be played: prompts, announcements and greetings. */
function useRecordings(tenantId: string) {
  const media = useQuery({
    queryKey: ['media', tenantId],
    queryFn: () => api.get<{ data: Array<{ id: string; kind: string; name: string; duration_sec: number | null }> }>(
      `/v1/tenants/${tenantId}/media`),
    enabled: !!tenantId,
  });
  return (media.data?.data ?? []).filter((m) => m.kind === 'prompt' || m.kind === 'announcement' || m.kind === 'greeting');
}

/**
 * A message's editor. In a call flow's side panel (`panel`) the result goes into the draft instead
 * of being saved, and may be unfinished: publishing is what refuses a message with no recording.
 */
export function MessageEditor({ tenantId, existing, onDone, panel, suggested, headAction }: {
  tenantId: string;
  existing: (Partial<FlowMessage> & { name: string }) | null;
  onDone: () => void;
  panel?: PanelMode;
  /** Words the AI flow builder wrote for a recording nobody has made yet (docs/41 §12). */
  suggested?: string | undefined;
  /** Beside the title on its own page: the message's History. */
  headAction?: ReactNode;
}) {
  const save = useSaveMessage(tenantId);
  const recordings = useRecordings(tenantId);
  const [name, setName] = useState(existing?.name ?? '');
  const [mediaId, setMediaId] = useState(existing?.media_id ?? '');
  const [next, setNext] = useState<Destination | null>(existing?.next_destination ?? null);
  const length = (m: { duration_sec: number | null }) => (m.duration_sec ? ` (${m.duration_sec.toFixed(1)}s)` : '');

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const body: FlowMessageUpsert = { name: name.trim(), media_id: mediaId, next_destination: next };
    if (panel) { panel.onApply({ ...body, media_id: mediaId || null }); return; }
    save.mutate({ ...(existing?.id ? { id: existing.id } : {}), ...body }, { onSuccess: onDone });
  };

  return (
    <form id={panel?.formId} onSubmit={submit}>
      {!panel && <EditorHead back="Call steps" onBack={onDone} title={existing?.id ? existing.name : 'New message'} action={headAction} />}
      <div className="arc-card arc-card-body" style={{ marginBottom: 14 }}>
        <div className="arc-grid arc-grid-2">
          <div className="arc-field" style={{ margin: 0 }}>
            <label className="arc-label" htmlFor="msg-name">Name</label>
            <input id="msg-name" className="arc-input" required maxLength={120} value={name}
              onChange={(e) => setName(e.target.value)} placeholder="After-hours notice" />
          </div>
          <div className="arc-field" style={{ margin: 0 }}>
            <label className="arc-label" htmlFor="msg-media">Recording</label>
            <select id="msg-media" className="arc-select" required={!panel} value={mediaId} onChange={(e) => setMediaId(e.target.value)}
              aria-invalid={panel && !mediaId ? true : undefined}>
              <option value="">Choose a recording…</option>
              {recordings.map((m) => <option key={m.id} value={m.id}>{m.name}{length(m)}</option>)}
            </select>
            <p className="arc-hint">
              {recordings.length === 0
                ? 'Nothing uploaded yet: add one under Media, then choose it here.'
                : 'Played in full. The caller cannot skip it.'}
            </p>
            {!mediaId && (
              <MakeRecordingAction tenantId={tenantId} kind="prompt" defaultName={name.trim() || 'New message'}
                suggested={suggested} onMade={(m) => setMediaId(m.id)} />
            )}
          </div>
        </div>
      </div>
      <div className="arc-card arc-card-body" style={{ marginBottom: 14 }}>
        <SubHead sub="Where the caller goes once the recording has played.">Then</SubHead>
        <div className="arc-field" style={{ margin: 0, maxWidth: 420 }}>
          <DestinationPicker tenantId={tenantId} value={next} onChange={setNext} allowNone />
          {!next && <p className="arc-hint">None: the call ends after the recording.</p>}
        </div>
      </div>
      {!panel && (
        <div className="arc-row" style={{ gap: 8 }}>
          <button type="submit" className="arc-btn arc-btn-primary" disabled={save.isPending || !name.trim() || !mediaId}>
            {save.isPending ? 'Saving…' : existing?.id ? 'Save changes' : 'Create message'}
          </button>
          <button type="button" className="arc-btn" onClick={onDone}>Cancel</button>
        </div>
      )}
      {save.error && <ErrorBanner error={save.error} />}
    </form>
  );
}

type HoursChoice = 'business' | 'schedule' | 'own';

/**
 * An hours check's editor: the account's business hours, another schedule, or hours of its own,
 * and where the call goes either way. `panel` as for MessageEditor.
 */
export function HoursCheckEditor({ tenantId, existing, onDone, panel, headAction }: {
  tenantId: string;
  existing: (Partial<HoursCheck> & { name: string }) | null;
  onDone: () => void;
  panel?: PanelMode;
  /** Beside the title on its own page: the check's History. */
  headAction?: ReactNode;
}) {
  const save = useSaveHoursCheck(tenantId);
  const schedules = useSchedules(tenantId);
  const business = useBusinessHours(tenantId);
  const businessId = business.data?.schedule_id ?? null;
  const [name, setName] = useState(existing?.name ?? '');
  const [mode, setMode] = useState<'schedule' | 'own'>(existing?.hours?.mode ?? 'schedule');
  const [scheduleId, setScheduleId] = useState(existing?.hours?.schedule_id ?? '');
  const [hours, setHours] = useState<HoursDraft>(() =>
    existing?.hours?.mode === 'own' ? hoursFromRules(existing.hours.rules) : blankHours());
  const [timezone, setTimezone] = useState(existing?.hours?.mode === 'own' ? existing.hours.timezone : '');
  const [open, setOpen] = useState<Destination | null>(existing?.open_destination ?? null);
  const [closed, setClosed] = useState<Destination | null>(existing?.closed_destination ?? null);

  const others = (schedules.data?.data ?? []).filter((s) => s.id !== businessId);
  const choice: HoursChoice = mode === 'own' ? 'own' : scheduleId && scheduleId === businessId ? 'business' : 'schedule';
  const pick = (c: HoursChoice) => {
    if (c === 'own') { setMode('own'); return; }
    setMode('schedule');
    setScheduleId(c === 'business' ? businessId ?? '' : scheduleId === businessId ? '' : scheduleId);
  };

  const missing: string[] = [];
  if (!name.trim()) missing.push('a name');
  if (mode === 'schedule' && !scheduleId) missing.push(choice === 'business' ? 'business hours for the account' : 'a schedule');
  if (mode === 'own' && invalidDay(hours)) missing.push('hours that close after they open');

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const body: HoursCheckUpsert = {
      name: name.trim(),
      hours: mode === 'own'
        ? { mode: 'own', timezone: timezone.trim() || null, rules: rulesFromHours(hours) }
        : { mode: 'schedule', schedule_id: scheduleId },
      open_destination: open, closed_destination: closed,
    };
    if (panel) { panel.onApply(body); return; }
    save.mutate({ ...(existing?.id ? { id: existing.id } : {}), ...body }, { onSuccess: onDone });
  };

  return (
    <form id={panel?.formId} onSubmit={submit}>
      {!panel && <EditorHead back="Call steps" onBack={onDone} title={existing?.id ? existing.name : 'New hours check'} action={headAction} />}
      <div className="arc-card arc-card-body" style={{ marginBottom: 14 }}>
        <div className="arc-field" style={{ maxWidth: 380 }}>
          <label className="arc-label" htmlFor="hc-name">Name</label>
          <input id="hc-name" className="arc-input" required maxLength={120} value={name}
            onChange={(e) => setName(e.target.value)} placeholder="Lunch cover" />
        </div>
        <SubHead sub="Asked when each call arrives, so a call at 17:01 is routed as closed.">Hours</SubHead>
        <Segmented label="Hours" value={choice}
          options={[['business', 'Business hours'], ['schedule', 'Another schedule'], ['own', 'Its own hours']]}
          onChange={pick} />
        {choice === 'business' && (
          <p className="arc-hint">
            {businessId
              ? `The account's business hours${business.data?.name ? ` (${business.data.name})` : ''}, set under Hours & schedules.`
              : 'This account has no business hours yet. Set them under Hours & schedules, or choose another schedule.'}
          </p>
        )}
        {choice === 'schedule' && (
          <div className="arc-field" style={{ marginTop: 12, maxWidth: 380 }}>
            <label className="arc-label" htmlFor="hc-schedule">Schedule</label>
            <select id="hc-schedule" className="arc-select" value={scheduleId} onChange={(e) => setScheduleId(e.target.value)}>
              <option value="">Choose a schedule…</option>
              {others.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
            {others.length === 0 && <p className="arc-hint">No other schedules yet. Add one under Hours &amp; schedules, or give this check its own hours.</p>}
          </div>
        )}
        {choice === 'own' && (
          <div style={{ marginTop: 12 }}>
            <div className="arc-field" style={{ maxWidth: 260 }}>
              <label className="arc-label" htmlFor="hc-tz">Timezone</label>
              <input id="hc-tz" className="arc-input" value={timezone} placeholder="The account's"
                onChange={(e) => setTimezone(e.target.value)} />
            </div>
            <HoursEditor idPrefix="hc-hours" value={hours} onChange={setHours} />
          </div>
        )}
        {existing?.hours?.open_now != null && (
          <p className="arc-hint">{existing.hours.open_now ? 'Open now' : 'Closed now'}, as saved.</p>
        )}
      </div>
      <div className="arc-card arc-card-body" style={{ marginBottom: 14 }}>
        <SubHead sub="Either may be left empty: the call ends on that branch.">Where calls go</SubHead>
        <div className="arc-route-dests">
          <span className="arc-route-dest-when">Open</span>
          <DestinationPicker tenantId={tenantId} value={open} onChange={setOpen} allowNone />
          <span className="arc-route-dest-when arc-muted">Closed</span>
          <DestinationPicker tenantId={tenantId} value={closed} onChange={setClosed} allowNone />
        </div>
      </div>
      <div className="arc-row" style={{ gap: 8, flexWrap: 'wrap' }}>
        {!panel && (
          <>
            <button type="submit" className="arc-btn arc-btn-primary" disabled={save.isPending || missing.length > 0}>
              {save.isPending ? 'Saving…' : existing?.id ? 'Save changes' : 'Create hours check'}
            </button>
            <button type="button" className="arc-btn" onClick={onDone}>Cancel</button>
          </>
        )}
        {missing.length > 0 && <span className="arc-subtle" style={{ fontSize: 12 }}>Needs {missing.join(', ')}.</span>}
      </div>
      {save.error && <ErrorBanner error={save.error} />}
    </form>
  );
}
